import { BehaviorSubject } from 'rxjs/BehaviorSubject';

import {
	ConvoAIResponseState,
	type ConvoAIStreamingState,
	getDefaultConvoAIStreamingState,
} from '../../models/ConvoAIStreamingState';
import { type ConvoAIRequestConfig } from './createConvoAIRequestConfig';
import { readStream } from './readConvoAIStream';

export function makeConvoAIStreamRequest(
	requestConfig: ConvoAIRequestConfig,
): BehaviorSubject<ConvoAIStreamingState> {
	const behaviorSubject = new BehaviorSubject<ConvoAIStreamingState>(
		getDefaultConvoAIStreamingState(),
	);

	fetch(requestConfig.url, {
		credentials: 'include',
		...requestConfig.requestInitParams,
	})
		.then((response) => readStream(response))
		.then(async (stream) => {
			let bufferContent = '';
			let answerChunkIndex = 0;
			for await (const chunk of stream) {
				const state = behaviorSubject.getValue();

				switch (chunk.type) {
					case 'ANSWER_PART':
						bufferContent += chunk.message.content;
						behaviorSubject.next({
							...state,
							responseState: ConvoAIResponseState.AnswerPart,
							content: bufferContent,
							answerChunkIndex,
						});
						answerChunkIndex += 1;
						break;
					case 'CHANNEL_ID':
						const channelId = chunk.message.content;
						behaviorSubject.next({
							...state,
							responseState: ConvoAIResponseState.ChannelId,
							content: channelId,
							answerChunkIndex: undefined,
						});
						break;
					case 'FINAL_RESPONSE':
						bufferContent = chunk.message.content;
						behaviorSubject.next({
							...state,
							responseState: ConvoAIResponseState.FinalResponse,
							content: bufferContent,
							answerChunkIndex: undefined,
						});
						break;
					case 'FOLLOW_UP_QUERIES':
						behaviorSubject.next({
							...state,
							responseState: ConvoAIResponseState.FollowUp,
							answerChunkIndex: undefined,
							followUpContent: chunk.message.follow_ups ?? [],
						});
						break;
					case 'TRACE':
						behaviorSubject.next({
							...state,
							responseState: ConvoAIResponseState.Trace,
							traceContent: chunk.message?.content ?? null,
							answerChunkIndex: undefined,
						});
						break;
					case 'ERROR': {
						behaviorSubject.next({
							...state,
							responseState: ConvoAIResponseState.Error,
							answerChunkIndex: undefined,
							error: { ...chunk },
						});
						break;
					}
				}
			}
		})
		.finally(() => {
			behaviorSubject.complete();
		});

	return behaviorSubject;
}
